import { api } from './client';

export interface BankOperationDto {
  id: number;
  bankAccountCode: string;
  currencyCode: string;
  currencyRate: number;
  uniqueSum: number;
  uniqueNumber: string;
  accountOperationCode: string;
  accountDebit: number;
  accountCredit: number;
  personId: number | null;
  /** The operation date (from the linked journal entry) — the date the payment happened. Date columns render THIS. */
  operationDate?: string;
  /** Row-creation timestamp in this DB — for imported statements it is the import day, NOT the operation date. */
  insertDate: string;
  updateDate: string;
  /** "Rankinis derinimas" marker — true = never auto-settled by the scanner, stays in Darbai */
  manualMatch: boolean;
  /** Why the payment needs manual matching (≤200 chars), e.g. "avansas" */
  manualMatchNote: string | null;
}

export interface CreateBankOperationRequest {
  bankAccountCode: string;
  currencyCode?: string;
  currencyRate: number;
  uniqueSum: number;
  uniqueNumber: string;
  accountOperationCode: string;
  accountDebit: number;
  accountCredit: number;
  personId?: number | null;
  operationDate?: string;
  autoSerialCode?: string;
}

export interface UpdateBankOperationRequest {
  bankAccountCode: string;
  currencyCode?: string;
  currencyRate: number;
  uniqueSum: number;
  uniqueNumber: string;
  accountDebit: number;
  accountCredit: number;
  personId?: number | null;
}

export interface BankOperationWithReconciliationDto extends BankOperationDto {
  isReconciled: boolean;
}

export const bankingApi = {
  list: (params: { from?: string; to?: string } = {}) =>
    api.get<BankOperationDto[]>('/bank-operations', {
      from: params.from,
      to: params.to,
    }),

  getById: (id: number) =>
    api.get<BankOperationDto>(`/bank-operations/${id}`),

  create: (data: CreateBankOperationRequest) =>
    api.post<number>('/bank-operations', data),

  update: (id: number, data: UpdateBankOperationRequest) =>
    api.put<void>(`/bank-operations/${id}`, data),

  delete: (id: number) =>
    api.delete<void>(`/bank-operations/${id}`),

  /** Get unreconciled bank operations for the reconciliation workspace */
  unreconciled: (params: { from?: string; to?: string } = {}) =>
    api.get<BankOperationWithReconciliationDto[]>('/bank-operations/unreconciled', {
      from: params.from,
      to: params.to,
    }),

  /** Toggle reconciliation status for a bank operation */
  toggleReconciliation: (id: number, isReconciled: boolean) =>
    api.put<void>(`/bank-operations/${id}/reconcile`, { isReconciled }),

  /**
   * Mark a bank payment for MANUAL matching ("Rankinis derinimas"): the
   * scanner will never auto-settle it — it stays in Darbai until matched by
   * hand. Server also drops the payment's automation-created settlement
   * links. Upsert: a second POST only updates the note.
   */
  setManualMatch: (id: number, note?: string) =>
    api.post<void>(`/bank-operations/${id}/manual-match`, { note: note ?? null }),

  /** Clear the manual-match marker — automatic matching resumes (idempotent) */
  clearManualMatch: (id: number) =>
    api.delete<void>(`/bank-operations/${id}/manual-match`),
};
